/**
 * Spatial indexing engine for viewport-based listing filtering.
 * Builds a grid-cell spatial index over listing coordinates for fast
 * viewport queries, with incremental (idle-scheduled) index construction
 * to avoid blocking the main thread.
 */

// ── Caches ──────────────────────────────────────────────────────────────────

const listingSpatialIndexCache = new WeakMap();
const listingSpatialIndexSignatureCache = new Map();

// Building the spatial index below this size costs more than the viewport filter it replaces.
// Bench (client/scripts/map-perf-bench.js) shows the crossover around ~450 listings.
const LISTING_SPATIAL_INDEX_MIN_SIZE = 450;
const LISTING_VISIBLE_REUSE_RATIO = 0.88;

// ── Viewport helpers ────────────────────────────────────────────────────────

const VIEWPORT_FILTER_MIN_LIST_SIZE = 180;
export const VIEWPORT_READY_GATING_MIN_LIST_SIZE = 180;

export function hasMeaningfulViewportChange(previous, next, epsilon = 0.0002) {
  if (!previous) return true;
  return (
    Math.abs(previous.north - next.north) > epsilon
    || Math.abs(previous.south - next.south) > epsilon
    || Math.abs(previous.east - next.east) > epsilon
    || Math.abs(previous.west - next.west) > epsilon
  );
}

function expandViewportBounds(viewportBounds) {
  const latPad = Math.max((viewportBounds.north - viewportBounds.south) * 0.35, 0.08);
  const lngPad = Math.max((viewportBounds.east - viewportBounds.west) * 0.35, 0.08);
  return {
    north: viewportBounds.north + latPad,
    south: viewportBounds.south - latPad,
    east: viewportBounds.east + lngPad,
    west: viewportBounds.west - lngPad,
  };
}

export function buildViewportRenderCandidates(list, viewportBounds) {
  if (!viewportBounds || list.length < VIEWPORT_FILTER_MIN_LIST_SIZE) return list;

  const expandedBounds = expandViewportBounds(viewportBounds);

  const matches = [];
  for (let i = 0; i < list.length; i += 1) {
    const listing = list[i];
    if (
      listing.lat <= expandedBounds.north
      && listing.lat >= expandedBounds.south
      && listing.lng <= expandedBounds.east
      && listing.lng >= expandedBounds.west
    ) {
      matches.push(listing);
    }
  }
  return matches;
}

// ── Idle scheduling ─────────────────────────────────────────────────────────

export function scheduleIdleWork(task, timeout = 80) {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    return {
      kind: 'idle',
      id: window.requestIdleCallback((deadline) => task(deadline), { timeout }),
    };
  }
  return {
    kind: 'timer',
    id: window.setTimeout(
      () =>
        task({
          didTimeout: true,
          timeRemaining: () => 0,
        }),
      16
    ),
  };
}

export function cancelIdleWork(handle) {
  if (!handle) return;
  if (handle.kind === 'idle' && typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function') {
    window.cancelIdleCallback(handle.id);
    return;
  }
  window.clearTimeout(handle.id);
}

// ── Signature / caching ─────────────────────────────────────────────────────

function toListingSignaturePart(listing) {
  if (!listing) return 'x';
  return `${listing.id ?? 'x'}:${Number(listing.lat ?? 0).toFixed(4)},${Number(listing.lng ?? 0).toFixed(4)}`;
}

export function buildListingSignature(list) {
  const total = list.length;
  if (total === 0) return '0';
  const first = toListingSignaturePart(list[0]);
  const mid = toListingSignaturePart(list[Math.floor(total / 2)]);
  const last = toListingSignaturePart(list[total - 1]);
  return `${total}|${first}|${mid}|${last}`;
}

export function rememberListingSignature(signature, index) {
  listingSpatialIndexSignatureCache.set(signature, index);
  if (listingSpatialIndexSignatureCache.size <= 6) return;
  const oldestKey = listingSpatialIndexSignatureCache.keys().next().value;
  if (oldestKey) listingSpatialIndexSignatureCache.delete(oldestKey);
}

export function getCachedSignatureIndex(signature) {
  return listingSpatialIndexSignatureCache.get(signature) || null;
}

export function setListingSpatialIndexCache(list, index) {
  listingSpatialIndexCache.set(list, index);
}

// ── Thresholds ──────────────────────────────────────────────────────────────

export function shouldBuildListingSpatialIndex(listLength) {
  return Number(listLength) >= LISTING_SPATIAL_INDEX_MIN_SIZE;
}

export function shouldEmitListingRenderProgressUpdate(previous, next, now, minIntervalMs = 160) {
  if (!next) return false;
  if (!previous) return true;
  if (next.done && !previous.done) return true;
  if (next.phase !== previous.phase) return true;
  if (next.total !== previous.total || next.visibleTotal !== previous.visibleTotal) return true;

  const renderedDelta = Math.max(0, (next.rendered ?? 0) - (previous.rendered ?? 0));
  const indexedDelta = Math.max(0, (next.indexed ?? 0) - (previous.indexed ?? 0));
  const visibleTotal = Number(next.visibleTotal ?? next.total ?? 0);
  const total = Number(next.total ?? 0);

  const renderingDeltaThreshold = Math.max(90, Math.min(220, Math.round(visibleTotal * 0.1)));
  const indexingDeltaThreshold = Math.max(180, Math.min(420, Math.round(total * 0.12)));

  if (next.phase === 'indexing') {
    if (indexedDelta >= indexingDeltaThreshold) return true;
  } else if (renderedDelta >= renderingDeltaThreshold) {
    return true;
  }

  return now - (previous.updatedAt || 0) >= minIntervalMs;
}

export function resolveInitialListingVisibleCount({
  currentVisibleCount,
  total,
  initialBatch,
  revisionChanged,
}) {
  if (total <= 0) return 0;

  const safeCurrentVisibleCount = Number(currentVisibleCount) || 0;
  const reuseThreshold = Math.floor(total * LISTING_VISIBLE_REUSE_RATIO);
  const canReusePreviousCount = !revisionChanged
    && safeCurrentVisibleCount > 0
    && safeCurrentVisibleCount <= reuseThreshold;

  if (canReusePreviousCount) return safeCurrentVisibleCount;
  return Math.min(initialBatch, total);
}

// ── Index construction ──────────────────────────────────────────────────────

function createEmptyListingSpatialIndex(list, cellSize) {
  return {
    buckets: new Map(),
    cellSize,
    list,
    latitudes: new Float64Array(list.length),
    longitudes: new Float64Array(list.length),
    queryMarks: null,
    queryToken: 1,
    queryMatches: null,
    lastQueryRevision: 0,
    lastQueryResultKind: null,
    coverageBounds: null,
  };
}

export function buildListingSpatialIndex(list, cellSize = 0.2) {
  const buckets = new Map();
  const invCellSize = 1 / cellSize;
  const latitudes = new Float64Array(list.length);
  const longitudes = new Float64Array(list.length);
  latitudes.fill(Number.NaN);
  longitudes.fill(Number.NaN);
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  for (let index = 0; index < list.length; index += 1) {
    const listing = list[index];
    if (listing?.lat == null || listing?.lng == null) continue;

    latitudes[index] = listing.lat;
    longitudes[index] = listing.lng;

    minLat = Math.min(minLat, listing.lat);
    maxLat = Math.max(maxLat, listing.lat);
    minLng = Math.min(minLng, listing.lng);
    maxLng = Math.max(maxLng, listing.lng);

    const latBucket = Math.floor(listing.lat * invCellSize);
    const lngBucket = Math.floor(listing.lng * invCellSize);

    let lngBuckets = buckets.get(latBucket);
    if (!lngBuckets) {
      lngBuckets = new Map();
      buckets.set(latBucket, lngBuckets);
    }

    const bucket = lngBuckets.get(lngBucket);
    if (bucket) {
      bucket.push(index);
    } else {
      lngBuckets.set(lngBucket, [index]);
    }
  }

  return {
    buckets,
    cellSize,
    list,
    latitudes,
    longitudes,
    queryMarks: null,
    queryToken: 1,
    queryMatches: null,
    lastQueryRevision: 0,
    lastQueryResultKind: null,
    coverageBounds: Number.isFinite(minLat)
      ? { north: maxLat, south: minLat, east: maxLng, west: minLng }
      : null,
  };
}

export function buildListingSpatialIndexIncremental(
  list,
  {
    cellSize = 0.2,
    chunkSize = 600,
    timeout = 80,
    onProgress = null,
    signal = null,
  } = {}
) {
  if (!Array.isArray(list) || list.length === 0) {
    onProgress?.({ processed: 0, total: 0, done: true });
    return Promise.resolve(createEmptyListingSpatialIndex(Array.isArray(list) ? list : [], cellSize));
  }

  return new Promise((resolve) => {
    const buckets = new Map();
    const total = list.length;
    const invCellSize = 1 / cellSize;
    const latitudes = new Float64Array(total);
    const longitudes = new Float64Array(total);
    latitudes.fill(Number.NaN);
    longitudes.fill(Number.NaN);
    let cursor = 0;
    let nextCheckpoint = Math.max(chunkSize, 1);
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;
    let cancelled = false;
    let scheduledHandle = null;
    let abortCleanup = null;

    const finalize = (result) => {
      if (abortCleanup) {
        abortCleanup();
        abortCleanup = null;
      }
      if (scheduledHandle) {
        cancelIdleWork(scheduledHandle);
        scheduledHandle = null;
      }
      resolve(result);
    };

    const handleAbort = () => {
      cancelled = true;
      onProgress?.({ processed: cursor, total, done: false, cancelled: true });
      finalize(null);
    };

    if (signal) {
      if (signal.aborted) {
        handleAbort();
        return;
      }
      signal.addEventListener('abort', handleAbort, { once: true });
      abortCleanup = () => signal.removeEventListener('abort', handleAbort);
    }

    const pump = (deadline) => {
      if (cancelled || signal?.aborted) {
        if (!cancelled) handleAbort();
        return;
      }

      const frameStart = Date.now();

      while (cursor < total) {
        const listing = list[cursor];
        if (listing?.lat != null && listing?.lng != null) {
          const lat = listing.lat;
          const lng = listing.lng;
          latitudes[cursor] = lat;
          longitudes[cursor] = lng;
          const latBucket = Math.floor(lat * invCellSize);
          const lngBucket = Math.floor(lng * invCellSize);

          if (lat < minLat) minLat = lat;
          if (lat > maxLat) maxLat = lat;
          if (lng < minLng) minLng = lng;
          if (lng > maxLng) maxLng = lng;

          let lngBuckets = buckets.get(latBucket);
          if (!lngBuckets) {
            lngBuckets = new Map();
            buckets.set(latBucket, lngBuckets);
          }

          const bucket = lngBuckets.get(lngBucket);
          if (bucket) {
            bucket.push(cursor);
          } else {
            lngBuckets.set(lngBucket, [cursor]);
          }
        }

        cursor += 1;

        if (cursor >= nextCheckpoint) {
          nextCheckpoint += chunkSize;
          const idleBudget = deadline?.timeRemaining ? deadline.timeRemaining() : 0;
          const elapsedMs = Date.now() - frameStart;
          const shouldYieldAfterChunk = deadline?.didTimeout
            ? true
            : idleBudget < 2 || elapsedMs >= 8;
          if (shouldYieldAfterChunk) {
            break;
          }
        }
      }

      onProgress?.({ processed: cursor, total, done: cursor >= total });

      if (cursor >= total) {
        const coverageBounds = Number.isFinite(minLat)
          ? { north: maxLat, south: minLat, east: maxLng, west: minLng }
          : null;
        finalize({
          buckets,
          cellSize,
          list,
          latitudes,
          longitudes,
          queryMarks: null,
          queryToken: 1,
          queryMatches: null,
          lastQueryRevision: 0,
          lastQueryResultKind: null,
          coverageBounds,
        });
        return;
      }

      scheduledHandle = scheduleIdleWork(pump, timeout);
    };

    scheduledHandle = scheduleIdleWork(pump, timeout);
  });
}

export function getCachedListingSpatialIndex(list, cellSize = 0.2) {
  if (!Array.isArray(list) || list.length === 0) return null;
  const cached = listingSpatialIndexCache.get(list);
  if (cached && cached.cellSize === cellSize) return cached;
  const built = buildListingSpatialIndex(list, cellSize);
  listingSpatialIndexCache.set(list, built);
  return built;
}

// ── Query ───────────────────────────────────────────────────────────────────

function shouldUseSparseMatchOrdering(matchCount, listLength) {
  if (matchCount <= 0 || listLength <= 0) return false;
  // Sorting sparse index hits beats scanning entire listing arrays on deep pans.
  if (matchCount <= 96) return true;
  return matchCount / listLength <= 0.08;
}

export function shouldIterateLongitudeEntries(lngBucketSpan, lngBucketCount) {
  if (lngBucketSpan <= 0 || lngBucketCount <= 0) return false;

  // Entry iteration avoids scanning large empty longitude spans when bucket maps are sparse.
  if (lngBucketSpan >= 24 && lngBucketSpan >= lngBucketCount * 2) return true;

  // For very wide pans, even moderately sparse rows are cheaper via entry iteration.
  if (lngBucketSpan >= 64 && lngBucketSpan > lngBucketCount + 10) return true;

  return false;
}

function isBucketFullyInsideBounds(bucket, cellSize, minBound, maxBound) {
  const bucketMin = bucket * cellSize;
  const bucketMax = bucketMin + cellSize;
  return bucketMin >= minBound && bucketMax <= maxBound;
}

function includesIndexCoverageBounds(expandedBounds, coverageBounds) {
  if (!coverageBounds) return false;
  return (
    expandedBounds.north >= coverageBounds.north
    && expandedBounds.south <= coverageBounds.south
    && expandedBounds.east >= coverageBounds.east
    && expandedBounds.west <= coverageBounds.west
  );
}

function updateQueryRevision(index, resultKind) {
  if (resultKind === 'partial') {
    index.lastQueryRevision = (index.lastQueryRevision || 0) + 1;
    index.lastQueryResultKind = resultKind;
    return;
  }

  if (index.lastQueryResultKind !== resultKind) {
    index.lastQueryRevision = (index.lastQueryRevision || 0) + 1;
    index.lastQueryResultKind = resultKind;
  }
}

export function queryListingSpatialIndex(index, viewportBounds) {
  if (!index || !viewportBounds) return [];

  const expandedBounds = expandViewportBounds(viewportBounds);
  const north = expandedBounds.north;
  const south = expandedBounds.south;
  const east = expandedBounds.east;
  const west = expandedBounds.west;

  if (includesIndexCoverageBounds(expandedBounds, index.coverageBounds)) {
    updateQueryRevision(index, 'full');
    return index.list;
  }

  const minLatBucket = Math.floor(south / index.cellSize);
  const maxLatBucket = Math.floor(north / index.cellSize);
  const minLngBucket = Math.floor(west / index.cellSize);
  const maxLngBucket = Math.floor(east / index.cellSize);

  if (!index.queryMarks || index.queryMarks.length !== index.list.length) {
    index.queryMarks = new Uint32Array(index.list.length);
    index.queryToken = 1;
  }

  if (!index.queryMatches || index.queryMatches.length !== index.list.length) {
    index.queryMatches = new Array(index.list.length);
  }

  if (!index.queryMatchIndices || index.queryMatchIndices.length !== index.list.length) {
    index.queryMatchIndices = new Uint32Array(index.list.length);
  }

  let token = (index.queryToken || 1) + 1;
  if (token >= 0xffffffff) {
    index.queryMarks.fill(0);
    token = 1;
  }
  index.queryToken = token;

  let matchedCount = 0;
  const listingList = index.list;
  const listingListLength = listingList.length;
  const latitudes = index.latitudes;
  const longitudes = index.longitudes;

  const lngBucketSpan = maxLngBucket - minLngBucket + 1;

  for (let latBucket = minLatBucket; latBucket <= maxLatBucket; latBucket += 1) {
    const lngBuckets = index.buckets.get(latBucket);
    if (!lngBuckets) continue;

    const latBucketInsideBounds = isBucketFullyInsideBounds(latBucket, index.cellSize, south, north);
    const iterateEntries = shouldIterateLongitudeEntries(lngBucketSpan, lngBuckets.size);

    if (iterateEntries) {
      for (const [lngBucket, bucket] of lngBuckets) {
        if (lngBucket < minLngBucket || lngBucket > maxLngBucket) continue;

        const lngBucketInsideBounds = isBucketFullyInsideBounds(lngBucket, index.cellSize, west, east);
        const canSkipCoordinateChecks = latBucketInsideBounds && lngBucketInsideBounds;

        for (let bucketIndex = 0; bucketIndex < bucket.length; bucketIndex += 1) {
          const listingIndex = bucket[bucketIndex];
          if (index.queryMarks[listingIndex] === token) continue;

          if (canSkipCoordinateChecks) {
            index.queryMarks[listingIndex] = token;
            index.queryMatchIndices[matchedCount] = listingIndex;
            matchedCount += 1;
            continue;
          }

          const lat = latitudes[listingIndex];
          if (Number.isNaN(lat)) continue;
          const lng = longitudes[listingIndex];
          if (
            lat <= north
            && lat >= south
            && lng <= east
            && lng >= west
          ) {
            index.queryMarks[listingIndex] = token;
            index.queryMatchIndices[matchedCount] = listingIndex;
            matchedCount += 1;
          }
        }
      }
      continue;
    }

    for (let lngBucket = minLngBucket; lngBucket <= maxLngBucket; lngBucket += 1) {
      const bucket = lngBuckets.get(lngBucket);
      if (!bucket) continue;

      const lngBucketInsideBounds = isBucketFullyInsideBounds(lngBucket, index.cellSize, west, east);
      const canSkipCoordinateChecks = latBucketInsideBounds && lngBucketInsideBounds;

      for (let bucketIndex = 0; bucketIndex < bucket.length; bucketIndex += 1) {
        const listingIndex = bucket[bucketIndex];
        if (index.queryMarks[listingIndex] === token) continue;

        if (canSkipCoordinateChecks) {
          index.queryMarks[listingIndex] = token;
          index.queryMatchIndices[matchedCount] = listingIndex;
          matchedCount += 1;
          continue;
        }

        const lat = latitudes[listingIndex];
        if (Number.isNaN(lat)) continue;
        const lng = longitudes[listingIndex];
        if (
          lat <= north
          && lat >= south
          && lng <= east
          && lng >= west
        ) {
          index.queryMarks[listingIndex] = token;
          index.queryMatchIndices[matchedCount] = listingIndex;
          matchedCount += 1;
        }
      }
    }
  }

  const orderedMatches = index.queryMatches;
  if (matchedCount === 0) {
    orderedMatches.length = 0;
    updateQueryRevision(index, 'empty');
    return orderedMatches;
  }

  if (matchedCount === listingListLength) {
    updateQueryRevision(index, 'full');
    return listingList;
  }

  if (shouldUseSparseMatchOrdering(matchedCount, listingListLength)) {
    const sortedIndices = index.queryMatchIndices.subarray(0, matchedCount);
    sortedIndices.sort();
    for (let i = 0; i < matchedCount; i += 1) {
      orderedMatches[i] = listingList[sortedIndices[i]];
    }
    orderedMatches.length = matchedCount;
    updateQueryRevision(index, 'partial');
    return orderedMatches;
  }

  let writeIndex = 0;
  for (let i = 0; i < listingListLength && writeIndex < matchedCount; i += 1) {
    if (index.queryMarks[i] !== token) continue;
    orderedMatches[writeIndex] = listingList[i];
    writeIndex += 1;
  }

  orderedMatches.length = writeIndex;
  updateQueryRevision(index, 'partial');
  return orderedMatches;
}
